import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, PenTool, MessageSquare, Users, Send, CalendarCheck,
  TrendingUp, TrendingDown, Minus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_saleshustler/artifacts/lbev3bte_Gemini_Generated_Image_r450fmr450fmr450.png";

const CATEGORIES = [
  { id: "posts", label: "Posts", icon: PenTool, target: 20 },
  { id: "comments", label: "Comments", icon: MessageSquare, target: 20 },
  { id: "connections", label: "Connections", icon: Users, target: 20 },
  { id: "messages", label: "Messages", icon: Send, target: 20 },
];

export default function HistoryPage({ user }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${API}/activities/history`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setHistory(data);
        }
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const formatWeekDate = (weekStart) => {
    const date = new Date(weekStart + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return Minus;
    if (current > previous) return TrendingUp;
    if (current < previous) return TrendingDown;
    return Minus;
  };

  const getTrendColor = (current, previous) => {
    if (!previous) return "text-muted-foreground";
    if (current > previous) return "text-secondary";
    if (current < previous) return "text-red-400";
    return "text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow p-8 rounded-full">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="history-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-card/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="IntegrityGO" className="h-8 w-auto" />
                <span className="font-semibold text-lg">Past Weeks</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Weekly History</h1>
          <p className="text-muted-foreground mt-1">
            Track your progress over time
          </p>
        </div>

        {history.length === 0 ? (
          <Card className="glass-panel border-white/10">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No activity history yet.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Start logging activities to see your weekly trends!
              </p>
              <Button
                className="mt-4 bg-primary hover:bg-primary/90"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {history.map((week, index) => {
              const previousWeek = history[index + 1];
              const totalTarget = 80; // 20 * 4
              const inputPercentage = Math.min((week.total_inputs / totalTarget) * 100, 100);
              
              return (
                <Card 
                  key={week.week_start} 
                  className="glass-panel border-white/10"
                  data-testid={`week-${week.week_start}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        Week of {formatWeekDate(week.week_start)}
                      </CardTitle>
                      {index === 0 && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Summary Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-white/5">
                        <p className="text-sm text-muted-foreground">Total Inputs</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono-display text-2xl text-primary">
                            {week.total_inputs}
                          </span>
                          {previousWeek && (
                            <span className={getTrendColor(week.total_inputs, previousWeek.total_inputs)}>
                              {React.createElement(
                                getTrendIcon(week.total_inputs, previousWeek.total_inputs),
                                { className: "h-4 w-4" }
                              )}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">/ {totalTarget} target</p>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-white/5">
                        <p className="text-sm text-muted-foreground">Meetings</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono-display text-2xl text-secondary">
                            {week.meetings}
                          </span>
                          {previousWeek && (
                            <span className={getTrendColor(week.meetings, previousWeek.meetings)}>
                              {React.createElement(
                                getTrendIcon(week.meetings, previousWeek.meetings),
                                { className: "h-4 w-4" }
                              )}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">booked</p>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-white/5 col-span-2 sm:col-span-1">
                        <p className="text-sm text-muted-foreground">Conversion</p>
                        <span className="font-mono-display text-2xl text-foreground">
                          {week.meetings > 0 
                            ? `${(week.total_inputs / week.meetings).toFixed(1)}:1`
                            : "—"
                          }
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">inputs per meeting</p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Input Progress</span>
                        <span className="text-foreground">{Math.round(inputPercentage)}%</span>
                      </div>
                      <Progress 
                        value={inputPercentage} 
                        className="h-2 bg-white/5"
                        indicatorClassName="progress-bar-cyan"
                      />
                    </div>

                    {/* Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {CATEGORIES.map((category) => {
                        const value = week[category.id] || 0;
                        const percentage = Math.min((value / category.target) * 100, 100);
                        const isComplete = percentage >= 100;
                        
                        return (
                          <div 
                            key={category.id}
                            className={`p-3 rounded-lg border ${
                              isComplete ? 'border-primary/30 bg-primary/10' : 'border-white/10 bg-white/5'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <category.icon className={`h-4 w-4 ${isComplete ? 'text-primary' : 'text-muted-foreground'}`} />
                              <span className="text-xs text-muted-foreground">{category.label}</span>
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className={`font-mono-display text-lg ${isComplete ? 'text-primary' : 'text-foreground'}`}>
                                {value}
                              </span>
                              <span className="text-xs text-muted-foreground">/ {category.target}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
